import axios from "axios";
import { create } from "zustand";
import { persist } from "zustand/middleware";

import { API_ROUTES, isLocalhost } from "@/constant";

let tenantPromise: Promise<string> | null = null;

export interface TenantStore {
  backend_api_url: string | null;
  country: string | null;

  setBackendApiUrl: (url: string) => void;

  updateState: (payload: Partial<TenantStore>) => void;

  clearTenant: () => void;

  getBackendApiUrl: () => Promise<string>;
}

export const useTenantStore = create<TenantStore>()(
  persist(
    (set, get) => ({
      backend_api_url: null,
      country: null,

      setBackendApiUrl: (url) => {
        set({
          backend_api_url: url,
        });
      },

      updateState: (payload) => {
        set((state) => ({
          ...state,
          ...payload,
        }));
      },

      clearTenant: () => {
        set({
          backend_api_url: null,
        });
      },

      getBackendApiUrl: async () => {
        if (tenantPromise) {
          return tenantPromise;
        }

        tenantPromise = (async () => {
          try {
            const { backend_api_url } = get();

            if (backend_api_url) {
              return backend_api_url;
            }

            const tenantDomain = isLocalhost
              ? new URL(String(process.env.AUTH_API_BASE_URL)).hostname
              : window.location.host;

            const { data } = await axios.get(
              `${process.env.AUTH_API_BASE_URL}/authentication${API_ROUTES.GET_TENANT_METADATA}`,
              {
                headers: {
                  "X-Tenant-Domain": tenantDomain,
                },
              },
            );
            const url = data.data.backend_api_url;
            const country = data.data.country.includes("Singapore") ? "SG" : "MY";

            const normalizedUrl = url.startsWith("http") ? url : `https://${url}`;

            set({
              backend_api_url: normalizedUrl,
              country,
            });

            return normalizedUrl;
          } catch (error) {
            console.error("Failed to resolve tenant", error);

            throw error;
          }
        })();

        try {
          return await tenantPromise;
        } finally {
          tenantPromise = null;
        }
      },
    }),
    {
      name: "tenant-storage",
    },
  ),
);
